import type {
  AfriexFeeAmount,
  BusinessFeeNegotiationLineRequest,
} from '@afriex/core/domain/types';
import {BUSINESS_FEE_NEGOTIATION_MAX_ROUNDS} from '@afriex/core/domain/types';
import {
  buildProposedFeeAmount,
  getCorridorKey,
  getFeeAmountKind,
  isSameFeeAmount,
  type NegotiationLineChange,
} from '@afriex/core/utils';

/** What a counter sends: the full set of lines and an optional note. */
export type FeeNegotiationCounter = {
  lines: BusinessFeeNegotiationLineRequest[];
  note?: string;
};

export const COUNTER_NOTE_MAX_LENGTH = 500;

/** Prices are cent-precise everywhere else, so a counter must be too. */
const hasMoreThanTwoDecimals = (text: string): boolean => {
  const value = Number(text);
  return Number(value.toFixed(2)) !== value;
};

/**
 * Checks Afriex's counter before it is sent. Texts are what the admin typed,
 * keyed by corridor; each is built into an amount against the line's live fee,
 * so a percentage corridor can only be countered as a percentage.
 */
export class FeeNegotiationCounterValidator {
  constructor(
    private lines: NegotiationLineChange[],
    private texts: Record<string, string>,
    private note: string,
    private roundCount: number,
  ) {}

  private amountFor(line: NegotiationLineChange): AfriexFeeAmount | undefined {
    const text = this.texts[getCorridorKey(line.corridor)] ?? '';
    return buildProposedFeeAmount(text, line.defaultAmount);
  }

  /** Why one line cannot be sent, by corridor key. Empty when all are fine. */
  lineErrors(): Record<string, string> {
    const errors: Record<string, string> = {};
    for (const line of this.lines) {
      const key = getCorridorKey(line.corridor);
      if (!this.amountFor(line)) {
        errors[key] =
          getFeeAmountKind(line.defaultAmount) === 'percent'
            ? 'Enter a percentage between 0 and 100.'
            : 'Enter a flat amount of 0 or more.';
      } else if (hasMoreThanTwoDecimals(this.texts[key] ?? '')) {
        errors[key] = 'Use at most 2 decimal places.';
      }
    }
    return errors;
  }

  /** Whether at least one line differs from what the merchant asked for. */
  hasChange(): boolean {
    return this.lines.some((line) => {
      const amount = this.amountFor(line);
      return amount && !isSameFeeAmount(amount, line.proposedAmount);
    });
  }

  /** The lines that differ from the merchant's ask, for the confirm step. */
  changedLines(): NegotiationLineChange[] {
    return this.lines.filter((line) => {
      const amount = this.amountFor(line);
      return amount && !isSameFeeAmount(amount, line.proposedAmount);
    });
  }

  /** The counter, or the first reason it cannot be sent. */
  value(): FeeNegotiationCounter {
    if (this.roundCount >= BUSINESS_FEE_NEGOTIATION_MAX_ROUNDS) {
      throw new Error('This is the final round. Accept or decline it.');
    }
    const [firstError] = Object.values(this.lineErrors());
    if (firstError) {
      throw new Error(firstError);
    }
    if (!this.hasChange()) {
      throw new Error('Change at least one price before sending a counter.');
    }
    const note = this.note.trim();
    if (note.length > COUNTER_NOTE_MAX_LENGTH) {
      throw new Error(
        `Keep the note under ${COUNTER_NOTE_MAX_LENGTH} characters.`,
      );
    }

    return {
      lines: this.lines.map((line) => ({
        corridor: line.corridor,
        proposedAmount: this.amountFor(line) ?? line.proposedAmount,
      })),
      ...(note ? {note} : {}),
    };
  }
}
